import {
	Breakpoints,
	BreakpointWrapper,
	CTA,
	Layout,
	LayoutRowGap,
	Text,
	TokenTextAppearance,
	TokenTextColor
} from '@volkswagen-onehub/components-core';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { OneFormState } from 'src/types';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronLeft, ChevronRight } from 'src/icons-core-imports';
import { Day } from 'src/types';
import {
	AllDaysWrapper,
	CalendarHandlerWrapper,
	Capitalize,
	DayPositionWrapper,
	DayWrapper,
	GreyText
} from './CalendarioCEMStyles';

interface CalendarioProps {
	name: string;
	preselectedDay?: Dayjs;
	fromDate?: Dayjs;
	toDate?: Dayjs;
	offsetStart?: number;
	offsetIgnoreDisables?: boolean;
	saturdayDisabled?: boolean;
	sundayDisabled?: boolean;
}

export function CalendarioCEM(props: CalendarioProps) {
	const dispatch = useDispatch();
	const today = dayjs(new Date());
	const {
		name,
		preselectedDay = today,
		fromDate = today, // primer dia seleccionable
		toDate = dayjs().add(1, 'month').endOf('month'), // ultimo dia seleccionable
		offsetStart = 1, // incremento de dias desde fromDate
		offsetIgnoreDisables = false, // ignoramos incremento en los dias deshabilitados
		saturdayDisabled = true, // deshabilitamos los sabados
		sundayDisabled = true // deshabilitamos los domingos
	} = props;
	const { formData } = useSelector((state: OneFormState) => state);
	const { fields } = formData;
	const fieldDay = fields[name] ? dayjs(fields[name], 'YYYY-MM-DD') : null;

	const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
	const [monthDays, setMonthDays] = useState<Day[]>(null);
	const [initDate, setInitDate] = useState(fromDate);
	const [showMonth, setShowMonth] = useState(initDate);
	const [selectedDay, setSelectedDay] = useState(preselectedDay);
	const tabsRef = useRef<HTMLDivElement>(null);


	const handleOffsetDays = () => {
		var date = initDate;
		// Marcamos fecha inicial con offsetStart (e ignoramos disabled?)
		var i = offsetStart;
		while (i > 0) {
			if (offsetIgnoreDisables) i--;
			else if (saturdayDisabled && date.day() === 6 || sundayDisabled && date.day() === 0) {
				// no offset decrement
			}
			else i--;
			// Arrastramos preselectedDay un dia más
			if (preselectedDay && preselectedDay.isSameOrBefore(date, 'day')) {
				setSelectedDay(date.add(1, 'day'));
				setShowMonth(date);
			}
			date = date.add(1, 'day');
		}
		setInitDate(date);

		if (fieldDay && !fieldDay.isSame(selectedDay, 'day')) {
			setSelectedDay(fieldDay);
		}
	};

	const addOneDay = () => {
		// Arrastramos initDate y si hace falta preselectedDay un dia más
		if (preselectedDay && preselectedDay.isSameOrBefore(initDate, 'day')) {
			setSelectedDay(initDate.add(1, 'day'));
		}
		setInitDate(initDate.add(1, 'day'));
		setShowMonth(initDate);
	}

	const handleBuildCalendar = () => {

		// Si la fecha inicial es sabado y estas disabled, saltamos día
		if (saturdayDisabled && initDate.day() === 6) {
			addOneDay();
			return;
		}
		// Si la fecha inicial es domingo y estas disabled, saltamos día
		if (sundayDisabled && initDate.day() === 0) {
			addOneDay();
			return;
		}

		let year = Number(showMonth.format('YYYY'));
		let month = Number(showMonth.format('M')) - 1;

		const firstDayOfMonth = dayjs().year(year).month(month).date(1);
		const firstDayInweek = firstDayOfMonth.format('d'); // The day of the week, with Sunday as 0
		const daysInMonth = dayjs().year(year).month(month).daysInMonth();

		let days: Day[] = [];

		// Se genera una array con espacios vacios para rellenar la primera fila del calendario y sincronizarla con la columna del dia de la semana
		if (firstDayInweek === '0') {
			days = new Array(6).fill('');
		} else {
			days = new Array(parseInt(firstDayInweek) - 1).fill('');
		}

		let date = firstDayOfMonth;

		for (let i = 1; i <= daysInMonth; i++) {
			const isActive = selectedDay ? selectedDay.isSame(date, 'day') : false;
			days.push(createDay(date, isActive));
			// Increment 1 day
			date = date.add(1, 'day');
		}

		setMonthDays(days);
	};

	const createDay = (date: Dayjs, active = false) => {
		const day: Day = {
			dayNumber: date.format('D'), // 1 - 31
			dayOfTheWeek: date.format('d'), // 0 - 6
			date: date.format('YYYY-MM-DD'),
			disabled: isDateDisabled(date),
			active: active,
		}
		return day;
	}

	const isDateDisabled = (date: any) => {
		if (sundayDisabled && date.day() === 0) {
			return true;
		}
		if (saturdayDisabled && date.day() === 6) {
			return true;
		}
		if (toDate && date.isAfter(toDate, 'day')) {
			return true;
		}
		if (isLast4Day(date)) {
			return true;
		}

		return date.isBefore(initDate, 'day');
	};

	const isLaborable = (date: any) => {
		return date.day() >= 1 && date.day() <= 5;
	};

	const isLast4Day = (date: any) => {
		// Cerrar calendario 4 dias laborables antes de terminar el mes
		let lastDays = [];
		let day;
		let offset = 0;
		while (lastDays.length < 4) {
			day = showMonth.endOf('month').subtract(offset, 'day');
			if (isLaborable(day)) {
				lastDays.push(day);
			}
			offset++;
		}

		return lastDays.some(day => date.isSame(day, 'day'));
	};

	const getOffsetMonth = (n: number) => {
		if (n > 0) {
			return showMonth.locale('es').add(n, 'month');
		}
		else if (n < 0) {
			return showMonth.locale('es').subtract(n * (-1), 'month');
		}
		else {
			return showMonth.locale('es');
		}
	}

	useEffect(() => { // Al inicio, realizar offsetDays
		handleOffsetDays();
	}, []);

	useEffect(() => { // Build calendar
		handleBuildCalendar();
	}, [initDate, selectedDay, showMonth]);

	const handleNextMonth = (e: any) => { // Carga del siguiente mes
		e.preventDefault();
		setShowMonth(getOffsetMonth(1));
	};

	const handlePreviousMonth = (e: any) => { // Carga del anterior mes
		e.preventDefault();
		setShowMonth(getOffsetMonth(-1));
	};

	const handleSelectDay = (day: Day) => {
		setSelectedDay(dayjs(day.date, 'YYYY-MM-DD')); // dayjs object
	};

	useEffect(() => {
		if (selectedDay) {
			dispatch({ type: 'UPDATE_FIELDS', payload: { [name]: selectedDay.format('YYYY-MM-DD') } });
		}
	}, [selectedDay]);

	const previousMonthDisabled = showMonth.isBefore(initDate, 'day') || showMonth.isSame(initDate, 'month');
	const nextMonthDisabled = toDate && showMonth.isAfter(toDate, 'day');

	return (
		<>
			<CalendarHandlerWrapper className="calendar-handler-wrapper">
				<CTA
					tag="button"
					emphasis="tertiary"
					onClick={handlePreviousMonth}
					disabled={previousMonthDisabled}
					ariaLabel="aria label"
					icon={<ChevronLeft />}
				/>
				<BreakpointWrapper min={Breakpoints.b960}>
					<Capitalize
						onClick={handlePreviousMonth}
						isDisabled={previousMonthDisabled}
					>
						{getOffsetMonth(-1).format('MMMM')}
					</Capitalize>
				</BreakpointWrapper>
				<Text bold appearance={TokenTextAppearance.copy200}>
					<Capitalize isDisabled={false} hasTitle>
						{showMonth.format('MMMM')} {showMonth.format('YYYY')}
					</Capitalize>
				</Text>
				<BreakpointWrapper min={Breakpoints.b960}>
					<Capitalize
						onClick={handleNextMonth}
						isDisabled={nextMonthDisabled}>
						{getOffsetMonth(1).format('MMMM')}
					</Capitalize>
				</BreakpointWrapper>
				<CTA
					tag="button"
					emphasis="tertiary"
					onClick={handleNextMonth}
					disabled={nextMonthDisabled}
					ariaLabel="aria label"
					icon={<ChevronRight />}
				/>
			</CalendarHandlerWrapper>
			<AllDaysWrapper className="all-days-wrapper">
				<Layout
					appearance={{
						[Breakpoints.default]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 3 },
							{ name: 'b', columns: 3 },
							{ name: 'c', columns: 3 },
							{ name: 'd', columns: 3 },
							{ name: 'e', columns: 3 },
							{ name: 'f', columns: 3 },
							{ name: 'g', columns: 3 },
							{ name: '.', columns: 2 },
						],
						[Breakpoints.b560]: [
							{ name: '.', columns: 2 },
							{ name: 'a', columns: 2 },
							{ name: '.', columns: 1 },
							{ name: 'b', columns: 2 },
							{ name: '.', columns: 1 },
							{ name: 'c', columns: 2 },
							{ name: '.', columns: 1 },
							{ name: 'd', columns: 2 },
							{ name: '.', columns: 1 },
							{ name: 'e', columns: 2 },
							{ name: '.', columns: 1 },
							{ name: 'f', columns: 2 },
							{ name: '.', columns: 1 },
							{ name: 'g', columns: 2 },
							{ name: '.', columns: 1 },
						],
						[Breakpoints.b960]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 3 },
							{ name: 'b', columns: 3 },
							{ name: 'c', columns: 3 },
							{ name: 'd', columns: 3 },
							{ name: 'e', columns: 3 },
							{ name: 'f', columns: 3 },
							{ name: 'g', columns: 3 },
							{ name: '.', columns: 2 },
						],
					}}
					rowGap={LayoutRowGap.static250}
				>
					{weekDays.map((day, index) => {
						if (index > 4) {
							return (
								<DayPositionWrapper>
									<DayWrapper key={index} className="day-wrapper">
										<Text bold appearance={TokenTextAppearance.copy200}>
											{day}
										</Text>
									</DayWrapper>
								</DayPositionWrapper>
							);
						} else {
							return (
								<DayPositionWrapper>
									<DayWrapper key={index} className="day-wrapper">
										<Text appearance={TokenTextAppearance.copy200}>
											<GreyText>{day}</GreyText>
										</Text>
									</DayWrapper>
								</DayPositionWrapper>
							);
						}
					})}
					{monthDays ? (monthDays.map((day, index) => {
						return (
							<DayPositionWrapper>
								<DayWrapper
									key={index}
									onClick={(e) => {
										e.preventDefault();

										if (!day.disabled) {
											if (tabsRef && tabsRef.current) {
												tabsRef.current.scrollIntoView({ behavior: 'smooth' });
											}
											handleSelectDay(day);
										}
									}}
									active={day.active}
									disabled={day.disabled}
									className="day-wrapper"
								>
									<Text appearance={TokenTextAppearance.copy200} color={TokenTextColor.inherit}>
										{day.dayNumber}
									</Text>
								</DayWrapper>
							</DayPositionWrapper>
						);
					})
					) : null}
				</Layout>
			</AllDaysWrapper>
		</>
	);
}
